import { useEffect, useMemo, useState } from 'react'
import { collection, doc, orderBy, query, serverTimestamp } from 'firebase/firestore'
import { addDoc, onSnapshot, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import type { ActivityEntry, Decision, DecisionStatus } from './types'

/**
 * The register.
 *
 * `enabled` is for the callers that only want the register as background — the roadmap
 * reads it to know which items are waiting on a decision. A role without `decisions.view`
 * gets a permission error from the listener rather than an empty list, so the roadmap
 * would surface a failure for a panel it was never going to show. Subscribing only when
 * the caller may read keeps that off the screen.
 */
export function useDecisions(enabled = true) {
  const { rows, loading, error } = useSharedCollection<Decision>(
    'decisions',
    (onRows, onError) => onSnapshot(
      query(collection(db, 'decisions'), orderBy('priorityRank'), orderBy('decisionId')),
      (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Decision)),
      onError,
    ),
    enabled,
  )
  return { decisions: rows, loading, error }
}

export function useActivity(decisionId: string | null) {
  const [entries, setEntries] = useState<ActivityEntry[]>([])

  useEffect(() => {
    if (!decisionId) { setEntries([]); return }
    return onSnapshot(
      query(collection(db, 'decisions', decisionId, 'activity'), orderBy('at', 'desc')),
      (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ActivityEntry)),
      () => setEntries([]),
    )
  }, [decisionId])

  return entries
}

type Actor = { email: string; name: string | null }

/**
 * Writes the answer, then records it in the change log. Separate writes rather than a
 * batch because the rules treat them as different capabilities, and because logging after
 * the update means a rejected write leaves no entry claiming something happened.
 */
export async function saveAnswer(
  decisionId: string,
  input: { finalDecision: string },
  actor: Actor,
  previous?: { finalDecision?: string },
) {
  await updateDoc(doc(db, 'decisions', decisionId), {
    finalDecision: input.finalDecision,
    answeredBy: actor.email,
    answeredAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  await recordChange(['decisions', decisionId], {
    field: 'Final decision',
    from: previous?.finalDecision ?? '',
    to: input.finalDecision || '(cleared)',
  }, actor)
}

/**
 * Moves a decision to another status, as its own write. It used to ride along with the
 * answer, which meant it could not be saved at all while the answer box was empty. The
 * answer and who gave it are left exactly as they were — `decisions.decide` covers this.
 */
export async function saveStatus(
  decisionId: string,
  status: DecisionStatus,
  actor: Actor,
  from?: DecisionStatus,
) {
  await updateDoc(doc(db, 'decisions', decisionId), { status, updatedAt: serverTimestamp() })
  await recordChange(['decisions', decisionId], { field: 'Status', from: from ?? '', to: status }, actor)
}

export async function addComment(
  decisionId: string,
  body: string,
  actor: Actor,
  parentId: string | null = null,
) {
  await addDoc(collection(db, 'decisions', decisionId, 'activity'), {
    kind: 'comment', body, byEmail: actor.email, byName: actor.name,
    // Stamped the same as `recordChange` (`src/lib/activity.ts`) stamps every other write
    // into an `activity` subcollection — without it this entry has no `parentKind`, and
    // the collection-group read at the foot of `firestore.rules` (gated on that field)
    // can never serve it, only the old per-parent read can.
    parentKind: 'decision',
    parentId, at: serverTimestamp(),
  })
}

/** Distinct values for the filter controls, derived from what is actually loaded. */
export function useFacets(decisions: Decision[]) {
  return useMemo(
    () => ({
      workstreams: [...new Set(decisions.map((d) => d.workstream).filter(Boolean))].sort(),
      owners: [...new Set(decisions.map((d) => d.owner).filter(Boolean))].sort(),
    }),
    [decisions],
  )
}
